import { ComponentType } from "react";

import { Button, Icon } from "@blueprintjs/core";
import styled from "@emotion/styled";
import { isBefore } from "date-fns";
import { Link } from "react-router";

import { pluralize } from "@/components/helpers/string-utils";
import { Box } from "@/components/layout/flexbox";
import {
  buildCreateScenarioUrl,
  buildScenarioUrl,
  SCENARIO_ASSIGNMENT_STATUS_STYLES,
} from "@/components/pages_logged_in/roleplays/utils";
import { RankIcon, RankText } from "@/components/pieces/roleplays/RankIcon";
import { CTA_COLOR, MIDTEXT_COLOR } from "@/css/constants";
import {
  ScenarioAssignmentDisplayStatus,
  UnifiedAssignmentType,
  VisibilityScope,
} from "@/graphql";
import { ListScenario } from "@/types/roleplays";
import { formatDate } from "@/utils/datetime";

import { SessionAttempts } from "./ScenarioAssignmentBanner";

interface ScenarioMiniCardProps {
  scenario: ListScenario;
  roleplaySlug?: string;
  isProgramRoleplay?: boolean;
  showShortDesc?: boolean;
  showDueDate?: boolean;
}

export function ScenarioMiniCard({
  scenario,
  roleplaySlug,
  isProgramRoleplay = false,
  showShortDesc = true,
  showDueDate = true,
}: ScenarioMiniCardProps) {
  const link = buildScenarioUrl({
    roleplaySlug,
    scenarioSlug: scenario.slug,
  });

  const statusText =
    isProgramRoleplay && scenario.visibilityScope === VisibilityScope.User
      ? "(Owner hid this scenario)"
      : "";

  const ContainerCard = getScenarioCardContainer(statusText ? Box : Link);
  const statusStyle =
    SCENARIO_ASSIGNMENT_STATUS_STYLES[
      ScenarioAssignmentDisplayStatus.NotStarted
    ];

  const earliestDueDate = scenario.unifiedAssignments
    ?.filter(
      (assignment): assignment is UnifiedAssignmentType => assignment !== null,
    )
    ?.reduce(
      (min, assignment) => {
        return assignment?.dueDate && min
          ? isBefore(new Date(assignment.dueDate), new Date(min))
            ? assignment.dueDate
            : min
          : assignment?.dueDate;
      },
      scenario.unifiedAssignments?.find((a) => a !== null)?.dueDate,
    );

  const renderAssignmentSubInfo = () => {
    const validAssignments = scenario.unifiedAssignments?.filter(
      (assignment): assignment is UnifiedAssignmentType => assignment !== null,
    );
    const haveNoAssignments = validAssignments && validAssignments.length === 0;
    const isHasOnlyOneAssignment =
      validAssignments && validAssignments.length === 1;

    const assignment = isHasOnlyOneAssignment ? validAssignments[0] : null;
    const isCompleted =
      assignment?.displayStatus === ScenarioAssignmentDisplayStatus.Completed;
    if (isHasOnlyOneAssignment && !isCompleted && assignment) {
      // Get properties that exist on both union types
      const completedCount = assignment.completedCount || 0;
      const displayStatus = assignment.displayStatus!;

      // Handle minAttempt and maxAttempt which may differ between union types
      const minAttempt = assignment.minAttempt || 0;
      const maxAttempt = assignment.maxAttempt || 0;

      return (
        <SessionAttempts
          completed={completedCount}
          minimum={minAttempt}
          maximum={maxAttempt}
          status={displayStatus}
        />
      );
    }
    if (isCompleted || haveNoAssignments) {
      return (
        <div className="flex items-center h-8">
          <div className="flex items-center">
            <div className="flex items-center min-w-5">
              <RankIcon rank={scenario.rank} size={22} />
            </div>
            <RankText ml={1} rank={scenario.rank}>
              {scenario.rank || "Not Started"}
            </RankText>
          </div>
        </div>
      );
    }
    return (
      <div className="text-sm text-gray-500">
        {pluralize({
          amount: scenario.unifiedAssignments?.length,
          plural: "assignments",
          singular: "assignment",
        })}
      </div>
    );
  };

  return (
    <ContainerCard to={link} className="bas round-corners">
      <Box>
        <Box fontWeight={500} fontSize={16} lineHeight="19px">
          {scenario.name} <span style={{ color: "red" }}>{statusText}</span>
        </Box>
        {scenario.shortDesc && showShortDesc ? (
          <Box
            mt={1}
            color={MIDTEXT_COLOR}
            minHeight={42}
            maxHeight={42}
            lineHeight="21px"
            display="block" // Changed from -webkit-box which isn't a standard display value
            overflow="hidden"
            fontSize={14}
            css={{
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
            }}
          >
            {scenario.shortDesc}
          </Box>
        ) : null}
        {earliestDueDate && showDueDate && (
          <div
            style={{ color: statusStyle?.textColor }}
            className="mt-1 text-sm"
          >
            Due: {formatDate(earliestDueDate, "MMM dd, yyyy")}
          </div>
        )}
      </Box>
      {renderAssignmentSubInfo()}
    </ContainerCard>
  );
}

export function CreateScenarioCard() {
  const ScenarioCardContainer = getScenarioCardContainer(Link);

  return (
    <ScenarioCardContainer
      to={buildCreateScenarioUrl()}
      className="bas round-corners"
    >
      <Box>
        <Box fontWeight={500} fontSize={16} lineHeight="19px">
          Create scenario
        </Box>
        <Box
          mt={1}
          color={MIDTEXT_COLOR}
          minHeight={42}
          maxHeight={42}
          lineHeight="21px"
          fontSize={14}
          overflow="hidden"
          css={{
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
          }}
        >
          Practice real situations and get detailed feedback
        </Box>
      </Box>
      <Box width={"100%"}>
        <Button
          intent="primary"
          fill={true}
          icon={<Icon icon="small-plus" />}
          onClick={() => null}
          style={{
            paddingTop: "4px",
            paddingBottom: "4px",
            borderRadius: "4px",
            lineHeight: "24px",
          }}
        >
          Create
        </Button>
      </Box>
    </ScenarioCardContainer>
  );
}

const getScenarioCardContainer = (
  component: ComponentType<ExplicitAny>,
) => styled(component)`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  background-color: #fff;
  flex-direction: column;
  color: inherit;
  padding: 16px;
  min-height: 147px;
  max-height: 147px;

  &:hover,
  &:focus,
  &:active {
    cursor: pointer;
    border-color: ${CTA_COLOR};
    box-shadow:
      0 1px 1px rgba(24, 32, 38, 0.1),
      0 2px 6px rgba(24, 32, 38, 0.05);
  }
`;
